/**
 * The deployed build's version, read from `/version.json`, against this bundle's.
 *
 * The build writes `version.json` (scripts/version-file.ts) with the same
 * string it bakes into `__APP_VERSION__`; a difference means a deploy has
 * shipped since this tab loaded. Every failure — a network error, the Vite dev
 * server's 404, an HTML fallback, a missing or empty version — reads as
 * "unknown" (null), never as a mismatch.
 */
import { api } from '@shared/services/api';

/** Where the build puts the file; served by nginx with `expires -1`. */
export const VERSION_URL = '/version.json';

/** The version baked into this bundle, or '' where the define is absent. */
export const BUNDLE_VERSION: string = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '';

/** Pull a non-empty `version` string out of the file's text, or null. */
export function parseDeployedVersion(text: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const { version } = parsed as { version?: unknown };
  return typeof version === 'string' && version.trim() !== '' ? version.trim() : null;
}

/**
 * Fetch the deployed version, bypassing every cache. Goes through `api.fetchRaw`
 * (the one client) because it accepts a `RequestInit`, which is what carries
 * `cache: 'no-store'`; any error, including a non-OK status, resolves null.
 */
export async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const response = await api.fetchRaw(VERSION_URL, { method: 'GET', cache: 'no-store' });
    return parseDeployedVersion(await response.text());
  } catch {
    return null;
  }
}

/** True only when both versions are known and they differ. */
export function isStaleBundle(deployed: string | null, bundle: string): boolean {
  return deployed !== null && bundle !== '' && deployed !== bundle;
}
